// ============================================
// src/BuyCoinsModal.jsx - mobile-first
// Buy coins via Paystack (NGN) or Flutterwave (USD), server-verified.
// Mirrors the dual-provider pattern in PaymentSystem.js's SubscriptionModal.
// ============================================
import React, { useState } from 'react';
import { X, Coins, Check, CreditCard } from 'lucide-react';
import { COIN_BUNDLES } from './CoinSystem';
import { initiatePaystackPaymentNGN } from './PaystackPayment';
import { initiateFlutterwavePayment } from './FlutterwavePayment';
import { verifyCoinsPurchase } from './serverApi';

const BuyCoinsModal = ({ user, onClose, onCoinsPurchased }) => {
  const [pendingBundle, setPendingBundle] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null); // 'flutterwave' | 'paystack'
  const [loading, setLoading] = useState(false);

  // Step 1: User clicks "Buy" on a bundle → show provider picker
  const handleBundleClick = (bundle) => {
    setPendingBundle(bundle);
  };

  // Step 2: User picks Flutterwave or Paystack → trigger that checkout
  const handleProviderChoice = (provider) => {
    const bundle = pendingBundle;
    if (!bundle) return;

    setSelectedProvider(provider);
    setLoading(true);

    const onPaymentSuccess = async (response) => {
      const reference = response.transaction_id || response.tx_ref || response.reference;

      // Server verifies with the provider, THEN credits coins
      const result = await verifyCoinsPurchase(reference, bundle.id, provider);

      setLoading(false);
      setPendingBundle(null);
      setSelectedProvider(null);

      if (result.success) {
        await onCoinsPurchased(result.newBalance);
        alert(`🎉 Success! ${bundle.coins} coins added. New balance: ${result.newBalance}`);
        onClose();
      } else {
        alert('Payment received but verification failed: ' + result.error + '\n\nContact support with reference: ' + reference);
      }
    };

    const onPaymentClose = () => {
      setLoading(false);
      setSelectedProvider(null);
    };

    if (provider === 'flutterwave') {
      initiateFlutterwavePayment(
        user.email,
        Math.round(bundle.priceUsd * 100), // Flutterwave takes cents
        bundle.id,
        user.name || user.email,
        onPaymentSuccess,
        onPaymentClose
      );
    } else if (provider === 'paystack') {
      initiatePaystackPaymentNGN(
        user.email,
        bundle.priceNgn,
        `${bundle.coins} Coins`,
        user.name || user.email,
        onPaymentSuccess,
        onPaymentClose
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start sm:items-center justify-center p-2 sm:p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-2 sm:my-4">
          <div className="p-3 sm:p-6">

            {/* Header */}
            <div className="flex justify-between items-start mb-3 sm:mb-6">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Coins className="w-5 h-5 text-yellow-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base sm:text-xl font-bold text-gray-800 truncate">
                    {pendingBundle ? 'Choose Payment Method' : 'Buy Coins'}
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {pendingBundle ? 'Select how you want to pay' : (
                      <>Balance: <strong>{user?.coins || 0} coins</strong></>
                    )}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1 flex-shrink-0 ml-2">
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* ── STEP 2: PROVIDER PICKER (shown after a bundle is selected) ── */}
            {pendingBundle ? (
              <div className="max-w-md mx-auto py-2 sm:py-6">
                <button
                  onClick={() => setPendingBundle(null)}
                  className="text-sm text-indigo-600 mb-4 hover:underline"
                >
                  ← Back to bundles
                </button>

                <div className="bg-gray-50 rounded-xl p-4 mb-6 text-center">
                  <p className="text-sm text-gray-600">Selected Bundle</p>
                  <p className="font-bold text-lg text-gray-800">
                    {pendingBundle.coins} Coins
                  </p>
                  <p className="text-2xl font-bold text-indigo-600">
                    ${pendingBundle.priceUsd.toFixed(2)}
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => handleProviderChoice('flutterwave')}
                    disabled={loading}
                    className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-xl hover:border-indigo-400 transition disabled:opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-gray-800">Flutterwave</p>
                        <p className="text-xs text-gray-500">Card, bank transfer, USSD • ${pendingBundle.priceUsd.toFixed(2)}</p>
                      </div>
                    </div>
                    {loading && selectedProvider === 'flutterwave' && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
                    )}
                  </button>

                  <button
                    onClick={() => handleProviderChoice('paystack')}
                    disabled={loading}
                    className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-xl hover:border-indigo-400 transition disabled:opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-gray-800">Paystack</p>
                        <p className="text-xs text-gray-500">
                          Card, bank transfer • ₦{pendingBundle.priceNgn.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {loading && selectedProvider === 'paystack' && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Info banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 sm:p-3 mb-3 sm:mb-4 text-xs sm:text-sm text-blue-800">
                  💡 Each review costs <strong>10 coins</strong>. Buy coins to keep your books visible and get more reviews.
                </div>

                {/* Bundles - 1 col on mobile, 2 on larger */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                  {COIN_BUNDLES.map(bundle => (
                    <div
                      key={bundle.id}
                      className={`relative border-2 rounded-xl p-3 sm:p-4 transition ${
                        bundle.popular ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200'
                      }`}
                    >
                      {bundle.popular && (
                        <span className="absolute -top-2 left-3 bg-indigo-600 text-white text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-semibold">
                          Most Popular
                        </span>
                      )}

                      <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Coins className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 flex-shrink-0" />
                          <span className="text-xl sm:text-2xl font-bold text-gray-800">{bundle.coins}</span>
                          <span className="text-xs sm:text-sm text-gray-500">coins</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-2">
                        <span className="text-base sm:text-lg font-bold text-gray-800">
                          ${bundle.priceUsd.toFixed(2)}
                        </span>
                        <button
                          onClick={() => handleBundleClick(bundle)}
                          disabled={loading}
                          className="bg-indigo-600 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-semibold hover:bg-indigo-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1 flex-shrink-0"
                        >
                          <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Buy
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="text-[10px] sm:text-xs text-gray-400 text-center mt-3 sm:mt-4">
                  Secure payment via Paystack or Flutterwave. Coins added instantly after payment.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyCoinsModal;
